import { Suspense, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Mic } from 'lucide-react';
import IntegrationIcon from '@/components/integrations/shared/IntegrationIcon';
import { getIntegrationDirectoryEntries } from '@/lib/integrationDirectoryEntries';
import { integrationSupportsAudioContributeOrLimited } from '@/integrations/capabilities/memoryModalities';
import { useAuth } from '@/context/AuthContext';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';

const DASHBOARD_INTEGRATIONS_HREF = '/dashboard?view=integrations';

export default function IntegrationsDirectoryGrid() {
  const { t } = useTranslation('integrations');
  const { isAuthenticated } = useAuth();
  const entries = useMemo(() => getIntegrationDirectoryEntries(t), [t]);

  return (
    <TooltipProvider>
      <ul className="grid grid-cols-2 gap-4 sm:grid-cols-3 sm:gap-5 md:grid-cols-4 md:gap-6 lg:grid-cols-5">
        {entries.map((item) => {
          const showAudio = integrationSupportsAudioContributeOrLimited(item.id);
          const href = isAuthenticated
            ? DASHBOARD_INTEGRATIONS_HREF
            : item.landingPagePath ?? '/login';
          return (
            <li key={item.id}>
              <Link
                to={href}
                className="group flex flex-col items-center rounded-xl border border-gray-200 bg-white/90 p-4 text-center shadow-sm transition hover:border-indigo-300 hover:shadow-md focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
                aria-label={t('directory.openIntegrationsFor', { name: item.displayName })}
              >
                <span className="mb-3 flex h-14 w-14 items-center justify-center rounded-lg bg-gray-50 transition group-hover:bg-indigo-50">
                  <Suspense
                    fallback={
                      <span
                        className="block h-9 w-9 animate-pulse rounded bg-gray-200"
                        aria-hidden
                      />
                    }
                  >
                    <IntegrationIcon icon={item.icon} className="h-9 w-9 text-gray-700" />
                  </Suspense>
                </span>
                <span className="flex min-h-[1.25rem] max-w-full flex-wrap items-center justify-center gap-1 text-sm font-medium text-gray-900 group-hover:text-indigo-800">
                  <span className="truncate">{item.displayName}</span>
                  {showAudio && (
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <span
                          className="inline-flex shrink-0"
                          tabIndex={0}
                          aria-label={t('memoryModalities.publicAudioHint')}
                        >
                          <Mic
                            className="h-3.5 w-3.5 text-indigo-600"
                            aria-hidden
                          />
                        </span>
                      </TooltipTrigger>
                      <TooltipContent>
                        <p className="max-w-xs text-sm">
                          {t('memoryModalities.publicAudioHint')}
                        </p>
                      </TooltipContent>
                    </Tooltip>
                  )}
                </span>
              </Link>
            </li>
          );
        })}
      </ul>
    </TooltipProvider>
  );
}
